<template>
    <v-container fluid>
        <v-card class="pt-8 mb-4">
            <v-row class="pl-7">
                <v-col class="pt-2" sm="8">
                    <v-btn color="primary" @click="startVetpointImport">
                        {{ $t('vetpointdaten.import.button') }}
                    </v-btn>
                    <v-col v-if="vetdataLog.anzahlVerarbeiteteArtikel == 0" class="pb-2 pl-1" sm="4">-</v-col>
                    <v-col v-else-if="!vetdataLog.istErfolgreich && vetdataLog.anzahlVerarbeiteteArtikel != 0" class="pb-2 pl-1" sm="4">{{
                        $t('vetpointdaten.import.letzterrun') + ' ' + vetdataLog.verarbeitetAm + ', ' + $t('vetpointdaten.import.status.laufend')
                    }}</v-col>
                    <v-col v-else class="pb-2 pl-1" sm="9">{{
                        $t('vetpointdaten.import.letzterrun') +
                        ' ' +
                        vetdataLog.verarbeitetAm +
                        ', ' +
                        $t('vetpointdaten.import.status.abgeschlossen') +
                        ', ' +
                        vetdataLog.fehlerMeldung
                    }}</v-col>
                </v-col>
            </v-row>
        </v-card>
        <v-row>
            <v-col>
                <!--table hauptkonfigurationen -->
                <v-infinite-scroll ref="infiniteScroll" @load="load">
                    <v-data-table-server
                        v-model:sort-by="sortBy"
                        :headers="headers"
                        :items="artikelList"
                        :loading="loading"
                        item-value="produktnummer"
                        :items-length="totalNumberOfRecords || 0"
                        hide-default-footer
                        class="elevation-1 nowhow-table"
                        :must-sort="true"
                        hover
                        :custom-sort="customSort"
                        @update:sort-by="onSortByChanged"
                    >
                        <!-- Header der Table(Filter anzeige, gefundene Elemente) -->
                        <template #top>
                            <TableHeader>
                                <template #default>
                                    <div class="table-header-item-count">
                                        {{ totalNumberOfRecords + ' ' + $t('archiv.table.header.items') }}
                                    </div>
                                    <v-text-field
                                        v-if="showSearchBar"
                                        v-model="filterData.searchQuery"
                                        class="ml-5"
                                        prepend-inner-icon="mdi-magnify"
                                        append-icon="mdi-close"
                                        :label="$t('vetpointdaten.suchfeld.title')"
                                        hide-details
                                        autofocus
                                        @update:model-value="searchFieldInputChanged"
                                        @click:append="searchBarClosed"
                                    ></v-text-field>
                                    <v-spacer />
                                    <v-tooltip top color="secondary">
                                        <template #activator="{ props }">
                                            <v-btn v-bind="props" icon color="primary">
                                                <v-icon v-if="!showSearchBar" @click="showSearchBar = !showSearchBar">mdi-magnify</v-icon>
                                            </v-btn>
                                        </template>
                                        <div>
                                            <span>
                                                {{ $t('tableheader.tooltip.search') }}
                                            </span>
                                        </div>
                                    </v-tooltip>
                                </template>
                            </TableHeader>
                        </template>
                        <!-- Selbst definierte Tablespalten -->
                        <template #item.produktnummer="{ item }">
                            <TextHighlights :text="item.produktnummer" :search-text="filterData.searchQuery" />
                        </template>
                        <template #item.produktname="{ item }">
                            <TextHighlights :text="item.produktname" :search-text="filterData.searchQuery" />
                        </template>
                        <template #item.swissmedicnummer="{ item }">
                            <TextHighlights :text="item.swissmedicnummer" :search-text="filterData.searchQuery" />
                        </template>
                        <template #item.atcvetcode="{ item }">
                            <TextHighlights :text="item.atcvetcode" :search-text="filterData.searchQuery" />
                        </template>
                    </v-data-table-server>
                </v-infinite-scroll>
            </v-col>
        </v-row>
    </v-container>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-facing-decorator';
import { namespace } from 'vuex-facing-decorator';
import debounce from 'lodash/debounce';

import { ArtikelFilterDto } from '@/types/ArtikelFilterDto';
import { ArtikelApiDto, VetdataVerarbeitungLogApiDto } from '@/api-schema';

import { NAME as NAME_TAKETEILNEHMER } from '../store';
const administrationModul = namespace(NAME_TAKETEILNEHMER);

import { NAME as NAME_SECURITY } from '@/store/modules/security';
const securityModul = namespace(NAME_SECURITY);

import { NAME as NAME_GENERAL } from '@/store/modules/general';
const generalModul = namespace(NAME_GENERAL);

import { NAME as NAME_HEADERNOTIFICATION } from '@/store/modules/headerNotification';
const headerModul = namespace(NAME_HEADERNOTIFICATION);

import { SUBTITLE, FOOTERTITLE } from '@/store/modules/general/mutations';
import { HASACCESS } from '@/store/modules/security/getters';
import { SETACCESSDENIED } from '@/store/modules/security/mutations';
import { NEW_ALERT } from '@/store/modules/headerNotification/actions';
import { APPLY_ARTIKELFILTER, CLEAR_ARTIKELFILTER } from '../store/mutations';
import { GET_ARTIKEL_LOADING, GET_ARTIKEL_DATA, GET_ARTIKEL_TOTAL_NUMBER_OF_RECORDS, GETVETDATALOG } from '../store/getters';
import { GET_ARTIKEL, GET_VETDATA_LOG, START_VETPOINT_IMPORT } from '../store/actions';
import TableHeader from '@/components/TableHeader.vue';
import TextHighlights from '@/components/TextHighlight/TextHighlights.vue';
@Component({
    components: {
        TableHeader,
        TextHighlights
    }
})
export default class VetpointDaten extends Vue {
    @administrationModul.Action(GET_ARTIKEL) getFilteredArtikel!: Function;
    @administrationModul.Action(GET_VETDATA_LOG) getVetdataImportLog!: Function;
    @administrationModul.Action(START_VETPOINT_IMPORT)
    startVetpointImport!: Function;

    @administrationModul.Getter(GET_ARTIKEL_LOADING) loading!: boolean;
    @administrationModul.Getter(GET_ARTIKEL_DATA)
    artikelList!: Array<ArtikelApiDto>;
    @administrationModul.Getter(GET_ARTIKEL_TOTAL_NUMBER_OF_RECORDS)
    totalNumberOfRecords!: number;
    @administrationModul.Getter(GETVETDATALOG)
    vetdataLog!: VetdataVerarbeitungLogApiDto;

    @administrationModul.Mutation(CLEAR_ARTIKELFILTER) clearFilter!: Function;
    @administrationModul.Mutation(APPLY_ARTIKELFILTER) applyFilters!: Function;

    @generalModul.Mutation(SUBTITLE) setSubTitle!: Function;
    @generalModul.Mutation(FOOTERTITLE) setFooterTitle!: Function;

    @securityModul.Getter(HASACCESS) hasAccess!: Function;
    @securityModul.Mutation(SETACCESSDENIED) setAccessDenied!: Function;

    @headerModul.Action(NEW_ALERT) alertMessage!: Function;

    get headers(): Array<any> {
        return [
            {
                title: this.$t('vetpointdaten.table.produktnummer'),
                key: 'produktnummer'
            },
            {
                title: this.$t('vetpointdaten.table.produktname'),
                key: 'produktname'
            },
            {
                title: this.$t('vetpointdaten.table.swissmedicnummer'),
                key: 'swissmedicnummer'
            },
            {
                title: this.$t('vetpointdaten.table.atcvetcode'),
                key: 'atcvetcode'
            },
            {
                title: this.$t('vetpointdaten.table.verfuegbarkeit'),
                key: 'verfuegbarkeit'
            }
        ];
    }
    sortBy: [{ key: 'produktnummer'; order: 'desc' }];
    filterData: ArtikelFilterDto = new ArtikelFilterDto();
    showSearchBar = false;
    infiniteLoad = null;

    /* Funktion deklarieren, aber erst in mount mit debounce initialisieren */
    searchFieldInputChanged!: () => void;

    created() {
        this.setSubTitle(this.$t('vetpointdaten.title'));
        this.setFooterTitle('S920');
    }
    mounted() {
        if (!this.hasAccess('Navigation.VetpointDaten')) {
            this.setAccessDenied(true);
            this.alertMessage({ text: this.$t('errormessage.noaccesspage') });
        } else {
            this.setAccessDenied(false);
            this.applyFilters(this.filterData);
            this.getVetdataImportLog();
            this.getFilteredArtikel(0);
        }
        this.searchFieldInputChanged = debounce(() => {
            this.applyFilters(this.filterData);
            this.getFilteredArtikel(0);
        }, 400);
    }

    private customSort(items: any[]) {
        return items;
    }

    private searchBarClosed() {
        this.showSearchBar = false;
        this.filterData.searchQuery = '';
        this.applyFilters(this.filterData);
        this.getFilteredArtikel(0);
    }

    async load({ done }) {
        console.log('loading triggered');
        if (!this.infiniteLoad) {
            this.infiniteLoad = done;
        }
        if (this.totalNumberOfRecords === null || this.totalNumberOfRecords === undefined) {
            // initialer datenload abwarten, sonst wird fälschlicherweise 'empty' emitted
            done('ok');
            return;
        }
        console.log('total number of records: ' + this.totalNumberOfRecords);
        console.log('listLength: ' + this.artikelList.length);

        await this.getFilteredArtikel(this.artikelList.length);
        if (this.artikelList.length >= this.totalNumberOfRecords) {
            done('empty');
            return;
        }
        done('ok');
    }

    onSortByChanged(newSortBy: any) {
        this.sortBy = newSortBy;
        if (this.sortBy.length > 0) {
            const sortField = this.sortBy[0];

            this.filterData.sortBy = sortField.key.charAt(0).toUpperCase() + sortField.key.slice(1);
            this.filterData.sortOrder = sortField.order === 'desc' ? 'Descending' : 'Ascending';
        } else {
            this.filterData.sortBy = null;
            this.filterData.sortOrder = null;
        }

        this.applyFilters(this.filterData);
        this.getFilteredArtikel(0);
    }
    @Watch('totalNumberOfRecords')
    onTotalNumberOfRecordsChanged() {
        if (this.infiniteLoad) {
            this.infiniteLoad('ok');
        }
    }
}
</script>
<style scoped>
.v-application a {
    color: #0d47a1;
    cursor: pointer;
}
</style>
